import React, { useState, useEffect } from 'react'
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
} from '@material-ui/core'
import { getOverlays } from '../../../Apis/overlays'
import OverlayEditor from './OverlayEditor'
import { positionStyle, DEFAULT_CROP } from './overlayLayout'
import { ORDER as CARD_TYPE_ORDER, isVisible, labelFor, SLUGS, variantLabelForSlug } from '../../../utils/constants/overlayTypes'
import { toBox } from './placementModel'
import { checkerboard } from './checkerboard'

// A board is identified by type AND variant: Team Shots brands home and away separately.
// Used only as the React list key — selection is driven by editingType/editingVariant.
export const boardKey = ({ type, variant }) => `${type}:${variant ?? 0}`

export const boardLabel = ({ type, variant }) =>
  variant
    ? `${labelFor(type)} (${variantLabelForSlug(SLUGS[type], variant)})`
    : labelFor(type)

export default function BrandingTab({ orgId, seasonId, editingType, editingVariant, onEditingChange }) {
  const [overlays, setOverlays] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const editing = overlays.find(
    (o) => o.type === editingType && (o.variant ?? 0) === (editingVariant ?? 0),
  ) || null

  // `silent` refreshes the list in place (no spinner), so the open editor stays mounted.
  const load = ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    return getOverlays(orgId, seasonId)
      // Legacy overlays store point-anchored placements (offsetX/Y, containerW, textAnchor,
      // maxLines). Normalize to the box model here so the editor always works with boxes,
      // regardless of when the overlay was saved.
      .then((items) => items.map((item) => (
        item.overlay?.placements
          ? { ...item, overlay: { ...item.overlay, placements: item.overlay.placements.map(toBox) } }
          : item
      )))
      .then(setOverlays)
      .catch((err) => {
        if (!silent) setError(err?.response?.data?.message || err?.message || 'Failed to load overlays')
      })
      .finally(() => {
        if (!silent) setLoading(false)
      })
  }

  useEffect(() => {
    if (orgId && seasonId) load()
  }, [orgId, seasonId])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Typography color='error' variant='body2'>{error}</Typography>
      </div>
    )
  }

  if (editing) {
    // Sibling rows of the open type, in variant order, so the editor can offer a side selector.
    const availableVariants = overlays
      .filter((o) => o.type === editing.type)
      .map((o) => o.variant ?? 0)
      .sort((a, b) => a - b)
    return (
      <OverlayEditor
        // Remount on every side switch so the editor's local state reseeds instead of carrying over.
        key={boardKey(editing)}
        mode='season'
        orgId={orgId}
        seasonId={seasonId}
        type={editing.type}
        variant={editing.variant}
        availableVariants={availableVariants}
        fields={editing.fields}
        initialOverlay={editing.overlay}
        supportsTargetMode={editing.supportsTargetMode}
        defaultTargetMode={editing.defaultTargetMode}
        onVariantChange={(nextVariant) => onEditingChange(editing.type, nextVariant)}
        onSave={() => load({ silent: true })}
        onCancel={() => { onEditingChange(null, 0); load() }}
      />
    )
  }

  return (
    <div style={{ padding: 16 }}>
      <Typography variant='h6' gutterBottom>
        Overlay Templates
      </Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
        Upload custom SVG templates and configure where each data field appears on screen.
      </Typography>
      <Grid container spacing={2}>
        {(() => {
          // One card per overlay TYPE, not per row: Team Shots' two variants share a card.
          const byType = overlays
            .filter((o) => isVisible(o.type))
            .reduce((acc, item) => {
              (acc[item.type] ??= []).push(item)
              return acc
            }, {})
          const cards = CARD_TYPE_ORDER
            .filter((type) => byType[type]?.length)
            .map((type) => ({
              type,
              variants: [...byType[type]].sort((a, b) => (a.variant ?? 0) - (b.variant ?? 0)),
            }))

          return cards.map(({ type, variants }) => {
            // Prefer a configured variant's artwork so a card with only one side branded
            // (e.g. Away only) doesn't render as blank.
            const thumbnailItem = variants.find((v) => v.overlay) ?? variants[0]
            // Presigned read URL — the platform S3 bucket is private, so the raw
            // canonical URL 403s for this thumbnail <img>.
            const svgUrl = thumbnailItem.overlay?.templateSvgReadUrl ?? thumbnailItem.overlay?.templateSvgUrl
            const layout = thumbnailItem.overlay?.templateLayout
            const crop = layout?.templateCrop ?? DEFAULT_CROP
            const hasCrop = !!(crop.top || crop.right || crop.bottom || crop.left)
            return (
              <Grid item xs={12} sm={6} md={4} key={type}>
                <Card
                  variant='outlined'
                  onClick={() => onEditingChange(type, variants[0].variant)}
                  style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
                >
                  <div
                    style={{
                      paddingTop: '56.25%',
                      ...checkerboard(8),
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    {svgUrl ? (
                      <img
                        src={svgUrl}
                        alt={boardLabel(thumbnailItem)}
                        style={{
                          ...positionStyle(
                            layout?.templatePosition ?? 'center',
                            layout?.templateScale ?? 100,
                            crop,
                            layout?.x,
                            layout?.y,
                          ),
                          pointerEvents: 'none',
                          ...(hasCrop && { clipPath: `inset(${crop.top}% ${crop.right}% ${crop.bottom}% ${crop.left}%)` }),
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant='body2' style={{ color: 'rgba(255,255,255,0.72)' }}>
                          Click to brand the overlay
                        </Typography>
                      </div>
                    )}
                  </div>
                  <CardContent style={{ paddingTop: 12, paddingBottom: 12, flexGrow: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
                        {labelFor(type)}
                      </Typography>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {variants.length > 1 ? (
                          // One chip per side — a single chip can't show both sides' state.
                          variants.map((v) => (
                            <Chip
                              key={boardKey(v)}
                              label={variantLabelForSlug(SLUGS[type], v.variant)}
                              size='small'
                              {...(v.overlay ? { color: 'primary' } : { variant: 'outlined' })}
                            />
                          ))
                        ) : (
                          thumbnailItem.overlay ? (
                            <Chip label='Configured' color='primary' size='small' />
                          ) : (
                            <Chip label='Default' size='small' variant='outlined' />
                          )
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            )
          })
        })()}
      </Grid>
    </div>
  )
}
